import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import type { ReviewComment } from "@/components/ai/review/types";
import { formatReviewCommentsForAgent } from "@/components/ai/review/use-review-comments";
import { Button } from "@/components/flexkit/Button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import {
    Agents,
    useCreateAgentExecution,
    useSendToAgentExecution,
    type PlainAgentExecution,
} from "@/queries/agent-queries";
import { pluralizeWithCount } from "@/utils/strings";
import {
    AgentCodeContext,
    AgentExecution_Phase,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import type { FileChange } from "gitpod-next-api/gitpod/v1/environment_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { GitPullRequest, Send } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { v4 as uuidv4 } from "uuid";

type CodeChangesPanelActionBarProps = {
    environmentId: string;
    agentExecution: PlainAgentExecution | null;
    commentsInChangeList: ReviewComment[];
    clearAllComments: () => void;
    changedFiles: readonly PlainMessage<FileChange>[];
};

export const CodeChangesPanelActionBar: FC<CodeChangesPanelActionBarProps> = ({
    environmentId,
    agentExecution,
    commentsInChangeList,
    clearAllComments,
    changedFiles,
}) => {
    const createAgentExecution = useCreateAgentExecution();
    const sendToAgentExecution = useSendToAgentExecution();

    const [isReviewLoading, setIsReviewLoading] = useState(false);
    const [isCreatePRLoading, setIsCreatePRLoading] = useState(false);
    const [isSubmitCommentsLoading, setIsSubmitCommentsLoading] = useState(false);

    const isAnyOperationLoading =
        isSubmitCommentsLoading || isReviewLoading || isCreatePRLoading || createAgentExecution.isPending;

    const isAgentBusy =
        agentExecution?.status?.phase === AgentExecution_Phase.RUNNING ||
        agentExecution?.status?.phase === AgentExecution_Phase.PENDING;

    const submittableCommentsCount = useMemo(
        () => commentsInChangeList.filter((c) => !c.thumbsDown).length,
        [commentsInChangeList],
    );

    const hasComments = commentsInChangeList.length > 0;
    const hasChangedFiles = changedFiles.length > 0;

    const handleSubmitComments = useCallback(async () => {
        const commentsToSubmit = commentsInChangeList.filter((c) => !c.thumbsDown);
        if (!agentExecution?.id || commentsToSubmit.length === 0) return;

        setIsSubmitCommentsLoading(true);
        try {
            const formattedComments = formatReviewCommentsForAgent(commentsToSubmit);

            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: agentExecution.id,
                    userInput: new UserInputBlock({
                        id: uuidv4(),
                        inputs: [
                            new UserInputBlock_Input({
                                input: {
                                    case: "text",
                                    value: new UserInputBlock_TextInput({
                                        content: formattedComments,
                                    }),
                                },
                            }),
                        ],
                    }),
                }),
            );
            clearAllComments();
        } catch (error) {
            console.error("Failed to submit comments:", error);
        } finally {
            setIsSubmitCommentsLoading(false);
        }
    }, [agentExecution?.id, commentsInChangeList, sendToAgentExecution, clearAllComments]);

    const handleStartReview = useCallback(async () => {
        if (!changedFiles.length) return;

        setIsReviewLoading(true);
        try {
            const fileList = changedFiles.map((f) => `- ${f.path}`).join("\n");
            const reviewCommand = `/ona:review\n\nFiles changed:\n${fileList}`;

            let executionId = agentExecution?.id;

            if (!executionId) {
                const { agentExecutionId } = await createAgentExecution.mutateAsync({
                    agentId: Agents.InEnvironmentOna.id,
                    codeContext: new AgentCodeContext({
                        context: {
                            case: "environmentId",
                            value: environmentId,
                        },
                    }),
                });
                executionId = agentExecutionId;
            }

            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: executionId,
                    userInput: new UserInputBlock({
                        id: uuidv4(),
                        inputs: [
                            new UserInputBlock_Input({
                                input: {
                                    case: "text",
                                    value: new UserInputBlock_TextInput({
                                        content: reviewCommand,
                                    }),
                                },
                            }),
                        ],
                    }),
                }),
            );
        } catch (error) {
            console.error("Failed to start review:", error);
        } finally {
            setIsReviewLoading(false);
        }
    }, [agentExecution?.id, changedFiles, sendToAgentExecution, createAgentExecution, environmentId]);

    const handleCreatePullRequest = useCallback(async () => {
        setIsCreatePRLoading(true);
        try {
            let executionId = agentExecution?.id;

            if (!executionId) {
                const { agentExecutionId } = await createAgentExecution.mutateAsync({
                    agentId: Agents.InEnvironmentOna.id,
                    codeContext: new AgentCodeContext({
                        context: {
                            case: "environmentId",
                            value: environmentId,
                        },
                    }),
                });
                executionId = agentExecutionId;
            }

            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: executionId,
                    userInput: new UserInputBlock({
                        id: uuidv4(),
                        inputs: [
                            new UserInputBlock_Input({
                                input: {
                                    case: "text",
                                    value: new UserInputBlock_TextInput({
                                        content: "Create a pull request for the current changes.",
                                    }),
                                },
                            }),
                        ],
                    }),
                }),
            );
        } catch (error) {
            console.error("Failed to create pull request:", error);
        } finally {
            setIsCreatePRLoading(false);
        }
    }, [agentExecution?.id, sendToAgentExecution, createAgentExecution, environmentId]);

    return (
        <>
            {/* Buttons — visible above @lg */}
            <div className="hidden items-center gap-2 @lg:flex">
                {hasComments && (
                    <div className="flex items-center">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSubmitComments}
                            disabled={isSubmitCommentsLoading || isAgentBusy || submittableCommentsCount === 0}
                            className="rounded-r-none"
                            data-tracking-id="send-comments-to-ona"
                        >
                            <Send size={16} className="shrink-0" />
                            <span>
                                {isSubmitCommentsLoading
                                    ? "Sending…"
                                    : `Send ${pluralizeWithCount(submittableCommentsCount, false, "comment")} to Ona`}
                            </span>
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="rounded-l-none border-l border-white/20 px-1.5"
                                    disabled={isSubmitCommentsLoading || isAgentBusy}
                                    data-tracking-id="send-comments-dropdown"
                                >
                                    <IconChevronDownSmall size="sm" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    variant="destructive"
                                    onClick={clearAllComments}
                                    data-tracking-id="dismiss-all-comments"
                                >
                                    Dismiss all comments
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
                {hasChangedFiles && (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleStartReview}
                        disabled={isAnyOperationLoading || isAgentBusy}
                        data-tracking-id="start-review-code-changes"
                    >
                        {isReviewLoading ? "Reviewing…" : "Review"}
                    </Button>
                )}
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleCreatePullRequest}
                    disabled={isAnyOperationLoading || isAgentBusy}
                    data-tracking-id="create-pull-request"
                >
                    <GitPullRequest size={16} className="shrink-0" />
                    <span>{isCreatePRLoading ? "Creating…" : "Create PR"}</span>
                </Button>
            </div>

            {/* Ellipsis menu — visible below @lg */}
            <div className="flex @lg:hidden">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            LeadingIcon={IconEllipsisHorizontal}
                            data-tracking-id="code-changes-actions-menu"
                        />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {hasComments && (
                            <DropdownMenuItem
                                onClick={handleSubmitComments}
                                disabled={isSubmitCommentsLoading || isAgentBusy || submittableCommentsCount === 0}
                                data-tracking-id="menu-send-comments-to-ona"
                            >
                                {isSubmitCommentsLoading
                                    ? "Sending…"
                                    : `Send ${pluralizeWithCount(submittableCommentsCount, false, "comment")} to Ona`}
                            </DropdownMenuItem>
                        )}
                        {hasChangedFiles && (
                            <DropdownMenuItem
                                onClick={handleStartReview}
                                disabled={isAnyOperationLoading || isAgentBusy}
                                data-tracking-id="menu-start-review-code-changes"
                            >
                                {isReviewLoading ? "Reviewing…" : "Review"}
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            onClick={handleCreatePullRequest}
                            disabled={isAnyOperationLoading || isAgentBusy}
                            data-tracking-id="menu-create-pull-request"
                        >
                            {isCreatePRLoading ? "Creating…" : "Create PR"}
                        </DropdownMenuItem>
                        {hasComments && (
                            <DropdownMenuItem
                                variant="destructive"
                                onClick={clearAllComments}
                                disabled={isSubmitCommentsLoading || isAgentBusy}
                                data-tracking-id="menu-dismiss-all-comments"
                            >
                                Dismiss all comments
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </>
    );
};
